import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LanguageRoundedIcon from '@mui/icons-material/LanguageRounded';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { usePlatform } from '../contexts/PlatformContext';
import { sitesPublicAPI } from '../services/api';
import ValliLoginShowcase, { LOGIN_GREEN, LOGIN_INK, ValliMark } from './ValliLoginShowcase';

const DIRECTORS_VALUE = 'DIRECTORS';
const ADMIN_VALUE = 'ADMIN';
const FINANCE_VALUE = 'FINANCE';
const normalizeEmail = (value) => String(value || '').trim().toLowerCase();
const isObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ''));

const clearPlatformStorage = () => {
  localStorage.removeItem('selectedSiteId');
  localStorage.removeItem('selectedSiteCode');
  localStorage.removeItem('selectedSiteName');
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    minHeight: 56,
    borderRadius: 2.2,
    bgcolor: '#fff',
    '& fieldset': { borderColor: '#d5d9dc' },
    '&:hover fieldset': { borderColor: '#9aa5a0' },
    '&.Mui-focused fieldset': { borderColor: LOGIN_GREEN, borderWidth: 1.5 },
  },
};

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { setPlatform } = usePlatform();
  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [selected, setSelected] = useState('');
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const response = await sitesPublicAPI.list();
        if (!mounted) return;
        const list = response?.data?.data || [];
        setSites(list);
        const remembered = localStorage.getItem('vf_last_platform') || '';
        const validIds = new Set(list.map((site) => String(site._id)));
        setSelected(
          [ADMIN_VALUE, DIRECTORS_VALUE, FINANCE_VALUE].includes(remembered) || validIds.has(remembered)
            ? remembered
            : ''
        );
      } catch (requestError) {
        if (mounted) setError(requestError?.response?.data?.message || 'Could not load sites. Please check your connection.');
      } finally {
        if (mounted) setLoadingSites(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const platformHint = useMemo(() => {
    if (selected === ADMIN_VALUE) return 'Administrator workspace';
    if (selected === DIRECTORS_VALUE) return 'Company-wide directors workspace';
    if (selected === FINANCE_VALUE) return 'Head office payments workspace';
    if (isObjectId(selected)) return 'Site workspace';
    return 'Select Admin, Directors, Finance or your site';
  }, [selected]);

  const onChangePlatform = (value) => {
    setSelected(value || '');
    localStorage.setItem('vf_last_platform', value || '');
    setError('');
  };

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    const loggingAsAdmin = selected === ADMIN_VALUE;
    const loggingAsDirector = selected === DIRECTORS_VALUE;
    const loggingAsFinance = selected === FINANCE_VALUE;
    const loggingAsSite = isObjectId(selected);

    if (!loggingAsAdmin && !loggingAsDirector && !loggingAsFinance && !loggingAsSite) {
      setError('Choose Admin, Directors, Finance or your site before signing in.');
      return;
    }
    if (!credentials.email.trim() || !credentials.password) {
      setError('Enter your email address and password.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await login({
        email: normalizeEmail(credentials.email),
        password: credentials.password,
        ...(loggingAsSite ? { siteId: selected } : {}),
      });
      if (!result?.success) {
        setError(result?.message || 'Login failed. Please check your credentials.');
        return;
      }

      let user = null;
      try { user = JSON.parse(localStorage.getItem('userData') || 'null'); } catch { user = null; }
      const role = String(user?.role || '').toLowerCase();

      if (loggingAsAdmin || role === 'admin') {
        clearPlatformStorage();
        setPlatform({ type: 'ADMIN' });
        localStorage.setItem('vf_last_platform', ADMIN_VALUE);
        navigate('/platform/admin/users', { replace: true });
        return;
      }
      if (loggingAsDirector || role === 'director') {
        clearPlatformStorage();
        setPlatform({ type: 'DIRECTORS' });
        localStorage.setItem('vf_last_platform', DIRECTORS_VALUE);
        navigate('/platform/directors/weekly-overview', { replace: true });
        return;
      }
      /* Accountants share the finance platform. They see the register, the
       * invoices behind it and the quarterly pack, and can change none of it —
       * enforced on the server, not by which screen they land on. Without this
       * they fell through to the site branch and were told their site details
       * were missing, which is true and completely unhelpful. */
      const headOfficeFinance = role === 'finance' || role === 'accountant';

      if (loggingAsFinance || headOfficeFinance) {
        if (!headOfficeFinance) {
          setError('This account is not a finance or accountant account.');
          return;
        }
        clearPlatformStorage();
        setPlatform({ type: 'FINANCE' });
        localStorage.setItem('vf_last_platform', FINANCE_VALUE);
        navigate('/platform/finance/overview', { replace: true });
        return;
      }

      const selectedSite = sites.find((site) => String(site._id) === String(selected)) || null;
      const userSite = user?.site && typeof user.site === 'object' ? user.site : null;
      const siteId = userSite?._id || selectedSite?._id || selected;
      const siteCode = userSite?.code || selectedSite?.code || '';
      const siteName = userSite?.name || selectedSite?.name || '';
      if (!siteId || !siteCode) {
        setError('Login succeeded but your site details are missing. Contact Admin.');
        return;
      }
      setPlatform({ type: 'SITE', siteId, siteCode, siteName });
      localStorage.setItem('vf_last_platform', String(siteId));
      localStorage.setItem('selectedSiteId', String(siteId));
      localStorage.setItem('selectedSiteCode', String(siteCode));
      localStorage.setItem('selectedSiteName', String(siteName));
      navigate(`/platform/site/${siteCode}/dashboard`, { replace: true });
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Login failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box className="vf-login-page" component="main" sx={{ minHeight: '100dvh', minWidth: 0, display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(660px, 1.58fr) minmax(440px, 1fr)' }, bgcolor: '#fff', overflow: { xs: 'auto', lg: 'hidden' } }}>
      <ValliLoginShowcase />

      <Box sx={{ minHeight: '100dvh', minWidth: 0, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', px: { xs: 1.75, sm: 5, lg: 5.5, xl: 8 }, py: { xs: 2.5, sm: 4 }, ml: { lg: '-5vw' }, pl: { lg: '8vw', xl: '9vw' }, bgcolor: '#fff', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', minWidth: 0, maxWidth: 470 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: { xs: 3, lg: 4.5 } }}>
            <Box sx={{ display: { xs: 'block', lg: 'none' }, minWidth: 0 }}><ValliMark compact dark /></Box>
            <Button className="vf-login-language" aria-label="Language: English" startIcon={<LanguageRoundedIcon />} variant="outlined" size="small" sx={{ ml: 1, flexShrink: 0, color: LOGIN_INK, borderColor: '#d8ddda', borderRadius: 2, textTransform: 'none' }}>
              English
            </Button>
          </Stack>

          <Box sx={{ display: { xs: 'none', lg: 'block' }, mb: 2.5 }}><ValliMark compact dark /></Box>
          <Typography sx={{ fontSize: { xs: 38, sm: 46 }, fontWeight: 950, letterSpacing: -1.8, color: LOGIN_INK, lineHeight: 1.05 }}>
            Welcome back
          </Typography>
          <Typography sx={{ mt: 1, color: '#46505a', fontSize: 16.5 }}>
            Sign in to access your Valli Forecourts workspace
          </Typography>

          {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2 }}>{error}</Alert>}

          <Box component="form" onSubmit={submit} noValidate sx={{ mt: 3.3 }}>
            <Stack spacing={2.2}>
              <FormControl fullWidth>
                <InputLabel id="platform-label">Access type or site</InputLabel>
                <Select
                  labelId="platform-label"
                  label="Access type or site"
                  value={selected}
                  onChange={(event) => onChangePlatform(event.target.value)}
                  disabled={loadingSites || submitting}
                  startAdornment={<InputAdornment position="start"><BusinessOutlinedIcon sx={{ color: '#758078' }} /></InputAdornment>}
                  sx={{ minHeight: 56, borderRadius: 2.2, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: LOGIN_GREEN } }}
                  renderValue={(value) => {
                    if (value === ADMIN_VALUE) return 'Admin';
                    if (value === DIRECTORS_VALUE) return 'Directors — all sites';
                    if (value === FINANCE_VALUE) return 'Finance — all sites';
                    const site = sites.find((item) => String(item._id) === String(value));
                    return site ? `${site.name} (${site.code})` : '';
                  }}
                >
                  <MenuItem value={ADMIN_VALUE}><Stack direction="row" spacing={1} alignItems="center"><SecurityOutlinedIcon fontSize="small" /><span>Admin</span></Stack></MenuItem>
                  <MenuItem value={DIRECTORS_VALUE}><Stack direction="row" spacing={1} alignItems="center"><PublicOutlinedIcon fontSize="small" /><span>Directors — All Sites</span></Stack></MenuItem>
                  <MenuItem value={FINANCE_VALUE}><Stack direction="row" spacing={1} alignItems="center"><AccountBalanceWalletOutlinedIcon fontSize="small" /><span>Finance — All Sites</span></Stack></MenuItem>
                  <Divider />
                  {loadingSites
                    ? <MenuItem disabled><CircularProgress size={17} sx={{ mr: 1 }} />Loading sites…</MenuItem>
                    : sites.map((site) => <MenuItem key={site._id} value={site._id}>{site.name} ({site.code})</MenuItem>)}
                </Select>
                <Typography variant="caption" sx={{ mt: 0.7, color: '#6b7670' }}>{platformHint}</Typography>
              </FormControl>

              <TextField
                required fullWidth label="Email address" type="email" value={credentials.email}
                onChange={(event) => setCredentials((current) => ({ ...current, email: event.target.value }))}
                disabled={submitting} autoComplete="email" sx={fieldSx}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailOutlinedIcon sx={{ color: '#758078' }} /></InputAdornment> }}
              />
              <TextField
                required fullWidth label="Password" type={showPassword ? 'text' : 'password'} value={credentials.password}
                onChange={(event) => setCredentials((current) => ({ ...current, password: event.target.value }))}
                disabled={submitting} autoComplete="current-password" sx={fieldSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><LockOutlinedIcon sx={{ color: '#758078' }} /></InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                        <IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((current) => !current)} edge="end">
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
              <Link component={RouterLink} to="/forgot-password" underline="hover" sx={{ color: LOGIN_GREEN, fontWeight: 800 }}>Forgot password?</Link>
            </Stack>

            <Button type="submit" fullWidth variant="contained" disabled={submitting}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <LockOutlinedIcon />}
              sx={{ mt: 2.4, minHeight: 55, borderRadius: 2.1, bgcolor: LOGIN_GREEN, fontSize: 16, fontWeight: 900, '&:hover': { bgcolor: '#056f23' }, boxShadow: '0 12px 24px rgba(7,135,43,.22)' }}>
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>

            <Stack direction="row" alignItems="center" spacing={1.4} sx={{ my: 2.4 }}>
              <Divider sx={{ flex: 1 }} /><Typography color="text.secondary">or</Typography><Divider sx={{ flex: 1 }} />
            </Stack>

            <Button fullWidth type="button" variant="outlined" startIcon={<InsightsOutlinedIcon />}
              onClick={() => {
                setError('');
                localStorage.removeItem('glToken');
                localStorage.removeItem('glUser');
                navigate('/gl-manager/login');
              }}
              sx={{ minHeight: 54, borderRadius: 2.1, borderColor: LOGIN_GREEN, color: '#086d26', fontWeight: 900, fontSize: 15.5, '&:hover': { borderColor: '#056f23', bgcolor: '#f2faf4' } }}>
              Golden Lines Manager
            </Button>

            <Stack direction="row" spacing={1.3} sx={{ mt: 2.8, p: 1.6, borderRadius: 2.2, bgcolor: '#f3faf5', border: '1px solid #d5e9d9' }}>
              <SecurityOutlinedIcon sx={{ color: LOGIN_GREEN }} />
              <Box>
                <Typography sx={{ color: '#135d29', fontWeight: 850, fontSize: 14 }}>Secure access for authorised users only.</Typography>
                <Typography sx={{ color: '#557060', fontSize: 12.8, mt: 0.2 }}>Your data is protected with enterprise-grade security.</Typography>
              </Box>
            </Stack>
          </Box>

          <Typography sx={{ mt: 3.5, textAlign: 'center', color: '#8c9390', fontSize: 12.5 }}>
            © {new Date().getFullYear()} Valli Forecourts. All rights reserved.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
